import { type AxiosError } from "axios";
import Vue from "vue";
import { Store } from "vuex";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { isModelStale, processCoreModelResponse } from "@/store/-utils";
import WishlistsApi from "@/store/shared-wishlists/-api";
import * as base64 from "@/utils/encoding/base64";
import { NOT_FOUND } from "@/utils/enums/http-codes";

export function decodeSharedCollectionHash(hash: string) {
  const decodedData = base64.decodeUtf8(hash);

  return decodedData.split(":");
}

const emptyFeedFactory: () => CollectionFeed = () => ({ ids: [], meta: null });

@Module({
  name: "shared-wishlists",
  stateFactory: true,
  namespaced: true,
})
export default class SharedWishlistsStore extends VuexModule {
  collections: CollectionMap = {};
  sharedWishItems: WishMap = {};
  feeds: CollectionFeeds = {};

  get getWishIdByLtkProductId() {
    return (ltkProductId: string) => {
      const wish = Object.values(this.sharedWishItems).find((item) => item?.ltkProductId === ltkProductId);
      return wish?.id;
    };
  }

  get collectionFeed() {
    return (feedName: string) => this.feeds[feedName] ?? emptyFeedFactory();
  }

  get getFeedHasMorePages() {
    return (feedName: string) => {
      const feed = this.collectionFeed(feedName);
      return (feed.ids.length === 0 && feed.meta === null) || Boolean(feed.meta?.next);
    };
  }

  @Action({ rawError: true })
  async fetchSharedCollection({ hash }: { hash: string }) {
    if (!isModelStale(this.collections[hash])) {
      return;
    }

    const [id, userId] = decodeSharedCollectionHash(hash);
    const api = new WishlistsApi(this.store.$axios);

    try {
      const result = await api.getSharedCollections({ id, userId });

      const collection = result.data.wishCollection;
      collection.fetchedAt = Date.now();

      this.setSharedCollection({ hash, collection });
    } catch (error) {
      if ((error as AxiosError)?.isAxiosError) {
        // Ignore missing resourse errors and allow pages how to deal with missing resources
        if ((error as AxiosError).response?.status === NOT_FOUND) {
          return;
        }
      }

      // Fail the action call and trigger unknown error handlers
      throw error;
    }
  }

  @Action({ rawError: true })
  async fetchSharedCollectionWishes({ hash, overwrite = false }: { hash: string; overwrite?: boolean }) {
    const { meta } = this.collectionFeed(hash);

    if (!this.getFeedHasMorePages(hash) && overwrite === false) {
      return;
    }

    const data: Parameters<WishlistsApi["getSharedCollectionWishes"]>[0] = {};

    if (overwrite === false && meta?.next !== undefined) {
      data.cursor = meta.next;
    }

    const api = new WishlistsApi(this.store.$axios);
    const [id, userId] = decodeSharedCollectionHash(hash);

    data.id = id;
    data.userId = userId;

    const response = await api.getSharedCollectionWishes(data);

    if (response.data.wishes.length > 0) {
      await processCoreModelResponse(this.store, response.data, true);
      const ids = response.data.wishes.map(({ id }) => id);

      this.setSharedCollectionWishesFeed({ hash, ids, meta: response.data.meta, overwrite });
    }
  }

  @Mutation
  setSharedCollection({ hash, collection }: { hash: string; collection: Collection }) {
    Vue.set(this.collections, hash, collection);
  }

  @Mutation
  setSharedCollectionWishes({ wishes }: { wishes: WishMap }) {
    Vue.set(this, "sharedWishItems", Object.assign({}, this.sharedWishItems, wishes));
  }

  @Mutation
  setSharedCollectionWishesFeed({
    hash,
    ids,
    meta,
    overwrite = false,
  }: {
    hash: string;
    ids: string[];
    meta?: WishlistMeta;
    overwrite?: boolean;
  }) {
    if (this.feeds[hash] === undefined) {
      Vue.set(this.feeds, hash, { ids: [], meta: null });
    }

    // Add items to feed
    const start = overwrite ? 0 : this.feeds[hash].ids.length;
    const length = overwrite ? this.feeds[hash].ids.length : 0;
    this.feeds[hash].ids.splice(start, length, ...ids);

    Vue.set(this.feeds[hash], "meta", meta ?? null);
  }
}

export const getSharedWishlistsStore = (store: Store<unknown>) => {
  const wishlistsStore = getModule(SharedWishlistsStore, store);

  return wishlistsStore;
};
